// Parity P1 wave 1: the task's Metrics block (hybrid design §3.1 "Metrics block: latest value,
// sparkline, + record (value, unit, note, observed at); backed by `observation` rows alongside
// the number field mirror"). One row per metric: its newest live value with unit, the series as
// a sparkline (oldest → newest, corrections replace what they supersede), and a history popover
// whose rows can be corrected. "+ Record" adds a value; agents record through `record_observation`.
import {
  Button,
  Field,
  FieldLabel,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Sparkline,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HistoryIcon, PlusIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { observationsQuery, p1Keys, type TaskObservation } from "./task-page/queries";

/**
 * Live rows (not superseded) per metric, oldest first by `observed_at`. A metric is its name
 * without case (as the API matches it); the first spelling seen names it.
 */
export function seriesByMetric(rows: TaskObservation[]): Map<string, TaskObservation[]> {
  const names = new Map<string, string>();
  const out = new Map<string, TaskObservation[]>();
  for (const r of rows) {
    if (r.superseded) continue;
    const lower = r.metric.toLowerCase();
    const name = names.get(lower) ?? r.metric;
    names.set(lower, name);
    const list = out.get(name) ?? [];
    list.push(r);
    out.set(name, list);
  }
  for (const list of out.values())
    list.sort((a, b) => a.observed_at.localeCompare(b.observed_at) || a.id.localeCompare(b.id));
  return out;
}

const fmt = (v: number) => v.toLocaleString(undefined, { maximumFractionDigits: 2 });
const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export function TaskMetrics({
  api,
  company,
  taskId,
  canEdit,
}: {
  api: Api;
  company: CompanyAccess;
  taskId: string;
  canEdit: boolean;
}) {
  const co = company.id;
  const q = useQuery(observationsQuery(api, co, taskId));
  const series = seriesByMetric(q.data?.data ?? []);
  if (q.isPending) return <p className="text-sm text-muted-foreground">Loading metrics…</p>;
  return (
    <div data-slot="task-metrics" className="flex flex-col gap-1">
      {[...series.entries()].map(([metric, rows]) => {
        const last = rows.at(-1);
        return (
          <div
            key={metric}
            data-metric={metric}
            className="flex min-h-9 items-center gap-3 border-b border-subtle"
          >
            <span className="min-w-0 flex-1 truncate text-sm">{metric}</span>
            <Sparkline
              values={rows.map((r) => r.value)}
              label={`${metric}: ${rows.length} observations`}
              className="h-6 w-24"
            />
            <span className="w-24 text-right text-sm font-medium tabular-nums">
              {last ? fmt(last.value) : "—"}
              {last?.unit ? (
                <span className="ml-1 text-xs font-normal text-muted-foreground">{last.unit}</span>
              ) : null}
            </span>
            <MetricHistory
              api={api}
              co={co}
              taskId={taskId}
              metric={metric}
              rows={rows}
              canEdit={canEdit}
            />
          </div>
        );
      })}
      {series.size === 0 ? (
        <p className="text-sm text-muted-foreground">
          No metrics yet: record a measured result (views, leads, response rate) to see its trend.
        </p>
      ) : null}
      {canEdit ? (
        <RecordObservation api={api} co={co} taskId={taskId} metrics={[...series.keys()]} />
      ) : null}
    </div>
  );
}

function MetricHistory({
  api,
  co,
  taskId,
  metric,
  rows,
  canEdit,
}: {
  api: Api;
  co: string;
  taskId: string;
  metric: string;
  rows: TaskObservation[];
  canEdit: boolean;
}) {
  return (
    <Popover>
      <PopoverTrigger
        render={<Button variant="ghost" size="icon-xs" aria-label={`${metric} history`} />}
      >
        <HistoryIcon aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <p className="mb-2 text-sm font-medium">{metric}</p>
        <ul aria-label={`${metric} observations`} className="m-0 flex list-none flex-col p-0">
          {[...rows].reverse().map((r) => (
            <li key={r.id} className="flex items-start gap-2 border-b border-subtle py-1.5">
              <span className="w-16 shrink-0 text-sm font-medium tabular-nums">
                {fmt(r.value)}
                {r.unit ? (
                  <span className="ml-0.5 text-xs text-muted-foreground">{r.unit}</span>
                ) : null}
              </span>
              <span className="min-w-0 flex-1 text-xs text-muted-foreground">
                {when(r.observed_at)}
                {r.actor.kind === "digital_employee" ? " · agent" : ""}
                {r.note ? <span className="block text-foreground">{r.note}</span> : null}
              </span>
              {canEdit ? (
                <RecordObservation
                  api={api}
                  co={co}
                  taskId={taskId}
                  metrics={[metric]}
                  correcting={r}
                />
              ) : null}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function RecordObservation({
  api,
  co,
  taskId,
  metrics,
  correcting,
}: {
  api: Api;
  co: string;
  taskId: string;
  metrics: string[];
  /** A row to correct: the new value supersedes it. */
  correcting?: TaskObservation;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [metric, setMetric] = useState(correcting?.metric ?? metrics[0] ?? "");
  const [value, setValue] = useState(correcting ? String(correcting.value) : "");
  const [unit, setUnit] = useState(correcting?.unit ?? "");
  const [note, setNote] = useState("");
  const [at, setAt] = useState("");
  const ids = { metric: useId(), value: useId(), unit: useId(), note: useId(), at: useId() };
  const listId = useId();
  const record = useMutation({
    mutationFn: () =>
      api.client.tasks.observations.create({
        params: { co, id: taskId },
        body: {
          metric: metric.trim(),
          value: Number(value),
          ...(unit.trim() ? { unit: unit.trim() } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
          ...(at ? { observed_at: new Date(at).toISOString() } : {}),
          ...(correcting ? { supersedes_id: correcting.id } : {}),
        },
      }),
    onSuccess: () => {
      setOpen(false);
      setValue("");
      setNote("");
      setAt("");
      toast.add({ title: correcting ? "Value corrected" : `${metric.trim()} recorded` });
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: p1Keys.observations(co, taskId) }),
  });
  const valid = metric.trim() !== "" && value.trim() !== "" && Number.isFinite(Number(value));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          correcting ? (
            <Button variant="ghost" size="xs" aria-label={`Correct ${fmt(correcting.value)}`} />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 mt-1 w-fit gap-1.5 text-muted-foreground hover:text-foreground"
            />
          )
        }
      >
        {correcting ? (
          "Correct"
        ) : (
          <>
            <PlusIcon aria-hidden="true" />
            Record
          </>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <form
          className="grid gap-3"
          aria-label={correcting ? "Correct an observation" : "Record an observation"}
          onSubmit={(e) => {
            e.preventDefault();
            if (valid && !record.isPending) record.mutate();
          }}
        >
          <Field>
            <FieldLabel htmlFor={ids.metric}>Metric</FieldLabel>
            <Input
              id={ids.metric}
              list={listId}
              value={metric}
              disabled={Boolean(correcting)}
              maxLength={80}
              placeholder="views_24h"
              onChange={(e) => setMetric(e.target.value)}
            />
            <datalist id={listId}>
              {metrics.map((m) => (
                <option key={m} value={m} />
              ))}
            </datalist>
          </Field>
          <div className="grid grid-cols-[1fr_6rem] gap-2">
            <Field>
              <FieldLabel htmlFor={ids.value}>Value</FieldLabel>
              <Input
                id={ids.value}
                type="number"
                step="any"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={ids.unit}>Unit</FieldLabel>
              <Input
                id={ids.unit}
                value={unit}
                maxLength={20}
                onChange={(e) => setUnit(e.target.value)}
              />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor={ids.at}>Observed at</FieldLabel>
            <Input
              id={ids.at}
              type="datetime-local"
              value={at}
              onChange={(e) => setAt(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.note}>Note</FieldLabel>
            <Textarea
              id={ids.note}
              rows={2}
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          <Button type="submit" size="sm" disabled={!valid || record.isPending}>
            {correcting ? "Save correction" : "Record"}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
